import { Alert, Box, Button, Card, Grid, Slider, Snackbar, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ChoosePreferences from './components/choosepreferences/ChoosePreferences';
import ViewQuote from './components/viewquote/ViewQuote';
import { useDispatch, useSelector } from 'react-redux';
import {
  downloadPricingQuote,
  fetchProductsPackagesV2,
  resetPricingQuoteSetupObject,
  resetPricingQuoteStates,
  resetUpdatePricingQuoteStates,
  selectOnboardingApplicationState,
} from '../pmui/onboarding/sagas/pmonboardingSlice';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '../pmui/transactions/Transactions';
import ChoosePackage from './components/choosepackage/ChoosePackage';
import SaveQuoteModal from './components/viewquote/SaveQuoteModal';
import DownloadIcon from '@mui/icons-material/Download';

export enum PricingPageStates {
  ChoosePackage = 'choose-package',
  ChoosePreferences = 'choose-preferences',
  ViewQuote = 'view-quote',
}

export default function PricingV2() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const query = useQuery();

  const currentPath = location.pathname.split('/').pop() as PricingPageStates;
  const selectedPackageUuid = query.get('packageUuid') || '';
  const pricingQuoteUuid = query.get('quoteUuid') || '';
  const { productsPackagesV2, isUpdatingPricingQuoteSuccess, isUpdatingPricingQuoteFailure, isDownloadingPricingQuoteFailure, isDownloadingPricingQuoteSuccess } =
    useSelector(selectOnboardingApplicationState);
  const allActiveProducts = productsPackagesV2?.Products || [];
  const selectedPackage = productsPackagesV2?.Packages.find((pkg) => pkg.PackageUuid === selectedPackageUuid);
  const isSelectedPackageCustom = selectedPackage?.PackageMetadata.IncludedProducts.length === 0;

  //local state
  const [activeTab, setActiveTab] = useState<PricingPageStates>(Object.values(PricingPageStates).includes(currentPath) ? currentPath : PricingPageStates.ChoosePackage);
  const [doorCount, setDoorCount] = useState(+(query.get('doors') || 1));
  const [openSaveQuoteModal, setOpenSaveQuoteModal] = useState(false);
  const [failureSnackBarOpen, setFailureSnackBarOpen] = useState(false);

  //useffects

  //onload fetch products and packages
  useEffect(() => {
    dispatch({ type: fetchProductsPackagesV2.type });
  }, [dispatch]);

  // Update activeTab when location changes
  useEffect(() => {
    if (Object.values(PricingPageStates).includes(currentPath)) {
      setActiveTab(currentPath);
    }
  }, [currentPath]);

  //once pricing quote updated with user details, close the save quote modal and open the print modal

  useEffect(() => {
    if (isUpdatingPricingQuoteSuccess) {
      setOpenSaveQuoteModal(false);
    }
  }, [isUpdatingPricingQuoteSuccess]);

  useEffect(() => {
    if (isUpdatingPricingQuoteSuccess && !openSaveQuoteModal) {
      dispatch({ type: downloadPricingQuote.type, payload: { pricingQuoteUuid: pricingQuoteUuid } });
      dispatch({ type: resetUpdatePricingQuoteStates.type });
    }
  }, [isUpdatingPricingQuoteSuccess, openSaveQuoteModal]);

  /**
   * Error handling for saving/updating pricing quote
   */
  useEffect(() => {
    if (isDownloadingPricingQuoteSuccess && isUpdatingPricingQuoteSuccess) {
      setFailureSnackBarOpen(false);
    } else if (isDownloadingPricingQuoteFailure || isUpdatingPricingQuoteFailure) {
      setFailureSnackBarOpen(true);
    }
  }, [isDownloadingPricingQuoteSuccess, isDownloadingPricingQuoteFailure, isUpdatingPricingQuoteFailure, isUpdatingPricingQuoteSuccess]);

  //handlers

  const handlePreviousTab = () => {
    if (activeTab === PricingPageStates.ChoosePreferences) {
      dispatch({
        type: resetPricingQuoteSetupObject.type,
      });
      history.push(`/pricing/${PricingPageStates.ChoosePackage}`);
    } else if (activeTab === PricingPageStates.ViewQuote) {
      dispatch({
        type: resetPricingQuoteStates.type,
      });
      history.push(`/pricing/${PricingPageStates.ChoosePreferences}?packageUuid=${selectedPackage?.PackageUuid}&doors=${doorCount}`);
    }
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setFailureSnackBarOpen(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#053742', p: 2 }}>
      <Card sx={{ p: 3, minHeight: '90vh' }}>
        {/* Tabs Header */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {Object.values(PricingPageStates).map((state, index) => (
            <React.Fragment key={state}>
              {/* Step Box */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: '10px',
                  textAlign: 'center',
                  bgcolor: activeTab === state ? '#39A2DB' : 'grey.300',
                  color: activeTab === state ? 'white' : 'black',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '180px',
                  maxWidth: '200px',
                  flex: '1 1 80px',
                  cursor: 'default',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontFamily: 'Work Sans !important',
                  }}
                >
                  {state
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
                    .join(' ')}
                </Typography>
              </Box>

              {/* Arrow */}
              {index < Object.values(PricingPageStates).length - 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                    position: 'relative',
                  }}
                >
                  <svg width="100%" height="20" viewBox="0 0 50 20" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                    <path d="M0 10H45L40 5M45 10L40 15" stroke="#9E9E9E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>

        <Grid container p={2} mt={4} mb={4}>
          <Grid item xs={0.5} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            {[PricingPageStates.ViewQuote, PricingPageStates.ChoosePreferences].includes(activeTab) && <ArrowBackIcon sx={{ color: '#39A2DB', cursor: 'pointer' }} onClick={handlePreviousTab} />}
          </Grid>
          <Grid item xs={11} sx={{ display: 'flex', alignItems: 'center' }}>
            {(activeTab === PricingPageStates.ChoosePackage || (activeTab === PricingPageStates.ChoosePreferences && isSelectedPackageCustom)) && (
              <React.Fragment>
                <Typography
                  sx={{
                    color: 'black',
                    fontWeight: '400',
                    fontFamily: 'Work Sans',
                    fontSize: '20px',
                    marginRight: '10px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  How many units/doors:
                </Typography>
                <TextField
                  value={doorCount}
                  type="number"
                  size="small"
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (value >= 1 || isNaN(value)) {
                      setDoorCount(value || 1);
                    }
                  }}
                  sx={{
                    backgroundColor: '#F1F0F5',
                    borderRadius: '8px',
                    marginRight: '20px',
                  }}
                  inputProps={{ maxLength: 100 }}
                  label="Required*"
                />
                <Slider value={doorCount} aria-label="Default" valueLabelDisplay="auto" onChange={(event: Event, newValue: number | number[]) => setDoorCount(newValue as number)} min={1} max={2000} />
              </React.Fragment>
            )}
          </Grid>
          <Grid item xs={0.5} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {[PricingPageStates.ViewQuote].includes(activeTab) && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="outlined"
                  sx={{
                    color: '#39A2DB',
                    border: '2px solid #39A2DB',
                    boxShadow: 'none',
                    borderRadius: '24px',
                    fontFamily: "'Open Sans', sans-serif",
                    fontSize: '14px',
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    '&:hover': {
                      backgroundColor: '#39A2DB',
                      color: 'white',
                      border: '2px solid #39A2DB',
                    },
                    width: '164px',
                    height: '40px',
                  }}
                  onClick={() => {
                    setOpenSaveQuoteModal(true);
                  }}
                  startIcon={<DownloadIcon />}
                >
                  Save Quote
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>

        <Switch>
          <Route exact path={url}>
            <Redirect to={`${url}/${PricingPageStates.ChoosePackage}`} />
          </Route>
          <Route path={`${url}/${PricingPageStates.ChoosePackage}`}>
            <ChoosePackage DoorCount={doorCount} />
          </Route>
          <Route path={`${url}/${PricingPageStates.ChoosePreferences}`}>
            {!!selectedPackage && allActiveProducts.length > 0 && <ChoosePreferences doorCount={doorCount} defaultProducts={allActiveProducts} selectedPackage={selectedPackage} />}
          </Route>
          <Route path={`${url}/${PricingPageStates.ViewQuote}`}>{!!selectedPackage && allActiveProducts.length > 0 && <ViewQuote />}</Route>
          <Redirect from="*" to={`${url}/${PricingPageStates.ChoosePackage}`} />
        </Switch>
      </Card>
      <SaveQuoteModal
        open={openSaveQuoteModal}
        onClose={() => {
          setOpenSaveQuoteModal(false);
        }}
        pricingQuoteUuid={pricingQuoteUuid}
      />
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={failureSnackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {'Something went wrong while saving the pricing quote. Please try again later or contact support. Thanks'}
        </Alert>
      </Snackbar>
    </Box>
  );
}
